import { GAME_VERSION } from './MetaManager';
import { PlayerProfile } from './PlayerProfile';

// === CLIENT LEADERBOARD ===
// Couche fine au-dessus de fetch() pour parler à l'API leaderboard.
// Gère aussi le consentement joueur (premier envoi → opt-in explicite).
// Si l'URL d'API n'est pas configurée, toutes les soumissions sont des no-ops
// silencieuses : le jeu fonctionne entièrement hors-ligne par défaut.

const CONSENT_KEY = 'deadtide_leaderboard_consent';
const SUBMIT_LOG_KEY = 'deadtide_leaderboard_last_submit';

export type LeaderboardConsent = 'accepted' | 'declined' | null;

export interface LeaderboardScore {
  pseudo: string;
  score: number;
  acte: number;
  etage: number;
  classe: string;
  version: string;
  duration: number;
  victory: boolean;
  createdAt: number;
}

export interface SubmitScoreInput {
  score: number;
  acte: number;
  etage: number;
  classe: string;
  duration: number;
  victory: boolean;
}

export interface SubmitResult {
  ok: boolean;
  rank?: number | null;
  error?: string;
  skipped?: 'no-api' | 'no-pseudo' | 'declined';
}

export type Period = 'all' | 'daily' | 'weekly' | 'monthly';

export interface ListOptions {
  limit?: number;
  acte?: number;
  period?: Period;
}

export interface ListResult {
  ok: boolean;
  scores: LeaderboardScore[];
  error?: string;
}

export class LeaderboardClient {
  /** URL de base de l'API (sans slash final). Vide = leaderboard désactivé. */
  static getApiUrl(): string {
    const raw = (import.meta as any).env?.VITE_LEADERBOARD_API_URL;
    if (typeof raw !== 'string') return '';
    return raw.trim().replace(/\/+$/, '');
  }

  static isEnabled(): boolean {
    return LeaderboardClient.getApiUrl().length > 0;
  }

  // === CONSENTEMENT ===

  static getConsent(): LeaderboardConsent {
    try {
      const v = localStorage.getItem(CONSENT_KEY);
      if (v === 'accepted' || v === 'declined') return v;
      return null;
    } catch {
      return null;
    }
  }

  static setConsent(value: 'accepted' | 'declined'): void {
    try { localStorage.setItem(CONSENT_KEY, value); } catch { /* */ }
  }

  static clearConsent(): void {
    try { localStorage.removeItem(CONSENT_KEY); } catch { /* */ }
  }

  // === SOUMISSION ===

  /**
   * Soumet un score. No-op si API non configurée, pseudo absent ou consentement refusé.
   * Si consentement non encore donné, retourne `{ skipped: 'declined' }` — l'appelant doit
   * d'abord demander le consentement via showConsentPopup() ou similaire.
   */
  static async submit(input: SubmitScoreInput): Promise<SubmitResult> {
    if (!LeaderboardClient.isEnabled()) return { ok: false, skipped: 'no-api' };

    const pseudo = PlayerProfile.getPseudo();
    if (!pseudo) return { ok: false, skipped: 'no-pseudo' };

    const consent = LeaderboardClient.getConsent();
    if (consent !== 'accepted') return { ok: false, skipped: 'declined' };

    const payload = {
      pseudo,
      score: Math.max(1, Math.round(input.score)),
      acte: Math.max(1, Math.round(input.acte)),
      etage: Math.max(1, Math.round(input.etage)),
      classe: input.classe,
      version: GAME_VERSION,
      duration: Math.max(30, Math.round(input.duration)),
      victory: !!input.victory,
      hash: await LeaderboardClient.computeHash(pseudo, input),
    };

    try {
      const res = await fetch(`${LeaderboardClient.getApiUrl()}/api/scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { ok: false, error: (json && json.error) || `HTTP ${res.status}` };
      }
      try { localStorage.setItem(SUBMIT_LOG_KEY, String(Date.now())); } catch { /* */ }
      return { ok: true, rank: json?.rank ?? null };
    } catch (e: any) {
      return { ok: false, error: e?.message ?? 'Network error' };
    }
  }

  // === LECTURE ===

  static async list(opts: ListOptions = {}): Promise<ListResult> {
    if (!LeaderboardClient.isEnabled()) return { ok: false, scores: [], error: 'Leaderboard désactivé' };

    const params = new URLSearchParams();
    params.set('limit', String(Math.min(100, Math.max(1, opts.limit ?? 50))));
    if (opts.acte) params.set('acte', String(opts.acte));
    if (opts.period && opts.period !== 'all') params.set('period', opts.period);

    try {
      const res = await fetch(`${LeaderboardClient.getApiUrl()}/api/scores?${params.toString()}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) {
        return { ok: false, scores: [], error: (json && json.error) || `HTTP ${res.status}` };
      }
      return { ok: true, scores: Array.isArray(json.scores) ? json.scores : [] };
    } catch (e: any) {
      return { ok: false, scores: [], error: e?.message ?? 'Network error' };
    }
  }

  static async findMe(pseudo: string, acte?: number): Promise<{ ok: boolean; rank?: number; score?: LeaderboardScore }> {
    if (!LeaderboardClient.isEnabled() || !pseudo) return { ok: false };
    const params = new URLSearchParams({ pseudo });
    if (acte) params.set('acte', String(acte));
    try {
      const res = await fetch(`${LeaderboardClient.getApiUrl()}/api/scores/me?${params.toString()}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok || !json.found) return { ok: false };
      return { ok: true, rank: json.rank, score: json.score };
    } catch {
      return { ok: false };
    }
  }

  // === HASH ANTI-SCRIPT ===
  // Reproduit côté client le hash que le serveur attend.
  // Le sel est exposé dans le bundle Vite — sécurité « papier alu » comme le webhook Discord.
  private static getSalt(): string {
    const raw = (import.meta as any).env?.VITE_LEADERBOARD_SUBMIT_SALT;
    return typeof raw === 'string' ? raw : '';
  }

  private static async computeHash(pseudo: string, p: SubmitScoreInput): Promise<string> {
    const salt = LeaderboardClient.getSalt();
    if (!salt) return '';
    const input = `${salt}|${pseudo}|${Math.round(p.score)}|${Math.round(p.acte)}|${Math.round(p.etage)}|${p.classe}|${GAME_VERSION}|${Math.round(p.duration)}|${p.victory ? 1 : 0}`;
    try {
      const buf = new TextEncoder().encode(input);
      const hash = await crypto.subtle.digest('SHA-256', buf);
      return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      return '';
    }
  }
}

// === FORMULE DE SCORE ===
// Centralisée ici pour que LeaderboardScene et RunEndScene partagent la même.
// Pondération choisie : l'étage est le facteur principal (progression), l'or et
// les HP restants sont des bonus secondaires, la victoire vaut un palier solide.
export interface ScoreContext {
  etage: number;          // 1-15
  gold: number;
  hpRemaining: number;    // HP du 1er vivant
  enemiesKilled: number;  // sur le run courant
  victory: boolean;
}

export function computeRunScore(ctx: ScoreContext): number {
  const floorPoints = Math.max(0, ctx.etage) * 100;
  const goldPoints = Math.max(0, ctx.gold);
  const killPoints = Math.max(0, ctx.enemiesKilled) * 15;
  const hpBonus = Math.max(0, ctx.hpRemaining) * 2;
  const winBonus = ctx.victory ? 500 : 0;
  return Math.max(1, floorPoints + goldPoints + killPoints + hpBonus + winBonus);
}
